import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  TooltipItem,
} from 'chart.js';

Chart.register(
  CategoryScale,
  LinearScale,
  LineElement,
  LineController,
  BarElement,
  BarController,
  PointElement,
  Title,
  Tooltip,
);

type DowntimeReasonInputData = {
  data: Array<{ reason: string; durationInSeconds: number; planned: boolean }>;
};

type ChartDataType = {
  x: string;
  y: number;
};
interface TooltipType extends TooltipItem<'bar' | 'line'> {
  raw: ChartDataType;
}

@Component({
  selector: 'thred-downtime-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './downtime-chart.component.html',
  styleUrl: './downtime-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DowntimeChartComponent implements OnChanges {
  @ViewChild('downtimeChart') set canvas(canvas: ElementRef) {
    if (canvas) {
      this.myCanvas.set(canvas);
      this.createChart(canvas);
    }
  }

  myCanvas = signal<ElementRef | null>(null);

  @Input() chartTitle = '';
  @Input() downtimeData: DowntimeReasonInputData = {
    data: [],
  };

  private chart: Chart<'bar' | 'line', ChartDataType[], unknown> | undefined;

  ngOnChanges(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    const canvas = this.myCanvas();
    if (canvas) {
      this.createChart(canvas);
    }
  }

  createChart(canvas: ElementRef<HTMLCanvasElement>) {
    const plannedDowntimeDurationInMins: ChartDataType[] = [];
    const unplannedDowntimeDurationInMins: ChartDataType[] = [];
    const downtimeCount: ChartDataType[] = [];

    const downtimeReasonToDurationAndCountMap = new Map<
      string,
      {
        plannedDurationsInSeconds: number;
        unplannedDurationsInSeconds: number;
        count: number;
      }
    >();
    for (const { reason, planned, durationInSeconds } of this.downtimeData
      .data) {
      if (downtimeReasonToDurationAndCountMap.has(reason)) {
        const data = downtimeReasonToDurationAndCountMap.get(reason);
        if (data) {
          data.count = data.count + 1;
          if (planned) {
            data.plannedDurationsInSeconds =
              data.plannedDurationsInSeconds + durationInSeconds;
          } else {
            data.unplannedDurationsInSeconds =
              data.unplannedDurationsInSeconds + durationInSeconds;
          }
          downtimeReasonToDurationAndCountMap.set(reason, data);
        } else {
          throw new Error('Impossible');
        }
      } else {
        downtimeReasonToDurationAndCountMap.set(reason, {
          count: 1,
          plannedDurationsInSeconds: planned ? durationInSeconds : 0,
          unplannedDurationsInSeconds: planned ? 0 : durationInSeconds,
        });
      }
    }

    const reasons = Array.from(downtimeReasonToDurationAndCountMap.keys());
    reasons.sort((r1, r2) => {
      const r1Data = downtimeReasonToDurationAndCountMap.get(r1);
      const r2Data = downtimeReasonToDurationAndCountMap.get(r2);
      if (!r1Data || !r2Data) {
        throw new Error('Impossible');
      } else {
        const r1Duration =
          r1Data.plannedDurationsInSeconds + r1Data.unplannedDurationsInSeconds;
        const r2Duration =
          r2Data.plannedDurationsInSeconds + r2Data.unplannedDurationsInSeconds;
        return r2Duration - r1Duration;
      }
    });
    for (const key of reasons) {
      const value = downtimeReasonToDurationAndCountMap.get(key);
      if (!value) {
        throw new Error('Impossible');
      }
      const { plannedDurationsInSeconds, unplannedDurationsInSeconds, count } =
        value;

      // Calculate durations in minutes
      const plannedDurationInMins = plannedDurationsInSeconds / 60;
      const unplannedDurationInMins = unplannedDurationsInSeconds / 60;

      // Push data to corresponding arrays
      plannedDowntimeDurationInMins.push({ x: key, y: plannedDurationInMins });
      unplannedDowntimeDurationInMins.push({
        x: key,
        y: unplannedDurationInMins,
      });
      downtimeCount.push({ x: key, y: count });
    }

    this.chart = new Chart(canvas.nativeElement, {
      data: {
        datasets: [
          {
            data: downtimeCount,
            label: 'Downtime Count',
            type: 'line',
            pointRadius: 7,
            pointHitRadius: 7,
            pointHoverRadius: 7,
            borderColor: '#737373',
            backgroundColor: '#737373',
            pointBackgroundColor: '#737373',
            pointBorderColor: '#737373',
            borderWidth: 0,
            pointBorderWidth: 0,
            spanGaps: false,
            yAxisID: 'y2',
          },
          {
            type: 'bar',
            data: plannedDowntimeDurationInMins,
            label: 'Planned Duration (Mins.)',
            backgroundColor: '#10645c',
            hoverBackgroundColor: '#10645c',
            hoverBorderWidth: 2,
            xAxisID: 'x',
            stack: 'a',
          },
          {
            type: 'bar',
            data: unplannedDowntimeDurationInMins,
            label: 'Unplanned Duration (Mins.)',
            backgroundColor: '#ad1b1b',
            hoverBackgroundColor: '#ad1b1b',
            hoverBorderWidth: 2,
            xAxisID: 'x',
            stack: 'a',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            enabled: true,
            displayColors: false,
            callbacks: {
              label: function (context: TooltipType) {
                return [
                  context.dataset.label + ': ' + context.raw.y.toString(),
                ];
              },
            },
          },
          title: {
            display: true,
            text: this.chartTitle,
            color: '#333333',
            font: {
              // size: isTablet ? 17 : 23,
              size: 23,
              weight: 'bold',
            },
          },
          legend: {
            display: false,
            position: 'bottom',
          },
        },
        animation: {
          duration: 0,
        },
        scales: {
          x: {
            grid: {
              drawOnChartArea: false,
            },
            offset: true,
            ticks: {
              display: true,
              autoSkip: false,
              maxRotation: 90,
              minRotation: 0,
            },
          },
          y: {
            title: {
              display: true,
              text: 'Duration (Mins.)',
            },
            type: 'linear',
            grid: {
              drawOnChartArea: true,
            },
            min: 0,
          },
          y2: {
            title: {
              display: true,
              text: 'Stop Count',
            },
            type: 'linear',
            position: 'right',
            grid: {
              drawOnChartArea: false,
            },
            min: 0,
            ticks: {
              // stepSize: 2,
            },
          },
        },
      },
    });
  }
}
